import moment from "moment";
import lodash from "lodash";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Button, CircularProgress, Divider, Grid, Table, TableBody, TableCell, TableRow, TextField } from "@mui/material";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker } from "@mui/x-date-pickers"
import { SALES_DOCUMENT_UPDATE } from "../../../../graphql/mutations";
import { GET_COMPANY_SALESDOCUMENT, INIT_SALES_DOCUMENTS_PRINT, SEARCH_COMPANY_CARDS } from "../../../../graphql/queries";
import { GET_ARTICLE_NO_LOOKUP, LOOKUP_ARTICLE_PRICE_LIST_ROWS } from "../../../../graphql/queries/CompanyAdminContext/SalesDocument";
import { useSnackbar } from "../../../../hooks";
import { FormattedCurrency } from "../../../common";
import SimpleModalCompact from "../../../common/ModalCompact/SimpleModalCompact";
import CompanyAddressAutoComplete from "./CompanyAddressAutoComplete";
import SalesDocumentRowTable from "./SalesDocumentRowTable";
import { useArticlePriceListCache } from "./useArticlePriceListCache.jsx";

const useStyles = makeStyles(theme => ({
  root: {
    "& .datePicker": {
      width: "100%",
    },
    "& .react-datepicker-wrapper": {
      textAlign: "center",
    },
    "& .react-datepicker__input-container input": {
      color: theme.palette.text.primary,
      height: "100%",
      width: "100%",
      border: "none",
      borderRadius: "4px 4px 0px 0",
      borderLeft: "none",
      padding: "23px 12px 6px 12px",
      fontSize: "12px",
      textAlign: "center",
      background: "rgba(0, 0, 0, 0.03)",
      marginTop: "0px",
      marginRight: "16px",
      borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
    },
    "& .react-datepicker-popper": {
      zIndex: 100,
    },
  },
  styleForTotal: {
    fontWeight: "bold",
  },
  invisibleCell: {
    border: "none",
    whiteSpace: "nowrap",
  },
  datePickerFormControl: {
    width: "100%",
  },
  table: {
    borderBottom: "none",
    "& tr:nth-child(3) :nth-of-type(2),& tr:nth-child(3) :nth-of-type(3)": {
      borderBottom: "1px solid rgba(0, 0, 0, 0.87)",
    },
    "& td:first-of-type": {
      width: "90%",
      borderBottom: "none",
    },
  },
}));

const addrBottom = { marginTop: "0px", marginBottom: "32px" };

const lastRowWidth = {
  display: "flex",
  minWidth: "fit-content",
  width: "60px",
  padding: "6px 2px",
  borderBottom: "none",
};

const SalesEditModal = ({
  companyId,
  handleClose,
  open,
  selectedSalesDocumentId,
  settings,
}) => {

  const intl = useIntl();
  const snackbar = useSnackbar();
  const classes = useStyles();

  const articlePriceListCache = useArticlePriceListCache();

  const [getArticleNoLookup, { data: articleNoLookupData, loading: loadingArticleNoLookupData, error: errorLoadingArticleNoLookupData }] = useLazyQuery(GET_ARTICLE_NO_LOOKUP);
  const [getCompanyCards, { data: companyData, loading: loadicCompanies, error: errorLoadingCompanies }] = useLazyQuery(SEARCH_COMPANY_CARDS);
  const [getCompanySalesDocument, { data: companySalesDocument, loading, error: errorLoading }] = useLazyQuery(GET_COMPANY_SALESDOCUMENT);
  const [initPrint, { data: urlData, loading: loadingUrl, error: errorPrint }] = useLazyQuery(INIT_SALES_DOCUMENTS_PRINT);
  const [lookupArticlePriceListRows, { data: articlePriceListRows, loading: loadingArticlePriceListRows, error: errorLoadingArticlePriceListRows }] = useLazyQuery(LOOKUP_ARTICLE_PRICE_LIST_ROWS);

  const [updateSalesDocument, { data: saveResult, loading: savingProgress, error: errorResult }] = useMutation(SALES_DOCUMENT_UPDATE);

  // [amountDebounceRef]
  //
  // Delays the price lookup until the user has stopped changing the amount
  // for 500 ms. This prevents intermediate values (e.g. "1" while typing "12")
  // from triggering a price update/dialog.
  //
  // The debounce does not replace the row change version check. The version
  // check is still required because the asynchronous price lookup may finish
  // after a newer amount has been entered.
  const amountDebounceRef = useRef({});

  // [pendingArticlePriceUpdateRef]
  //
  // Prevents multiple price-update dialogs from being opened at the same time.
  //
  // Price lookups are asynchronous, so more than one lookup can complete
  // while a price-update dialog is already pending. Once a dialog has been
  // opened, further price-update requests are ignored until the user accepts
  // or rejects the pending update.
  const pendingArticlePriceUpdateRef = useRef(false);

  // [rowChangeVersionRef]
  //
  // Tracks the latest amount/article change per row.
  //
  // Price lookups are asynchronous. A user can change the amount again
  // before an earlier lookup has completed. The version is captured when
  // starting a lookup and checked when it completes, so stale results
  // cannot update the row or open the price-update dialog.
  const rowChangeVersionRef = useRef({});

  const [articleNos, setArticleNos] = useState();
  const [companyCards, setCompanyCards] = useState();
  const [document, setDocument] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pendingArticlePriceUpdate, setPendingArticlePriceUpdate] = useState(null);
  const [selectedCompanyCard, setSelectedCompanyCard] = useState({ companyName: '' });
  const [sumValues, setSumValues] = useState({ sum: 0, vat: 0, rounding: 0, total: 0 });

  const initializeNewDocument = () => {
    var newDocument = {
      salesDocumentId: -1,
      documentType: "INVOICE",
      header: {
        amountDue: 0,
        documentDate: new Date(),
        dueDate: moment(new Date()).add(30, "days").toDate(),
        paymentTermsDays: 30,
        ourReference: "",
        address: {
          address1: "",
          address2: "",
          address3: "",
          address4: "",
          address5: "",
          city: "",
          contact: "",
          country: "",
          customerId: "",
          email: "",
          reference: "",
          telephone: "",
          zip: "",
        },
      },
      rows: [
        {
          articleNo: "",
          amount: 1,
          description: "",
          price: 0,
          total: 0,
          unit: "PIECES",
          discount: 0,
          vAT: 25.0,
          priceDirty: false,
        },
      ]
    };

    setDocument(newDocument);
  };

  const getRowChangeVersion = (index) =>
    rowChangeVersionRef.current[index] ?? 0;

  const bumpRowChangeVersion = (index) => {
    // Every new article/amount change invalidates any previous
    // asynchronous price lookup for this row.
    const nextVersion = getRowChangeVersion(index) + 1;
    rowChangeVersionRef.current[index] = nextVersion;
    return nextVersion;
  };

  const lookupArticlePrice = async (articleNo) => {
    const cached = articlePriceListCache.get(articleNo);

    if (cached) {
      return cached;
    }

    const { data } = await lookupArticlePriceListRows({
      variables: {
        companyId,
        articleNo,
      },
    });

    const rows =
      data?.companyAdminContext?.salesDocuments
        ?.lookupArticlePriceListRows ?? [];

    articlePriceListCache.set(articleNo, rows);

    return articlePriceListCache.get(articleNo);
  };

  const clearAmountDebounces = () => {
    Object.values(amountDebounceRef.current).forEach(timeoutId => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });

    amountDebounceRef.current = {};
  };

  useEffect(() => {
    return () => {
      clearAmountDebounces();
    };
  }, []);

  useEffect(() => {
    if (!open) {
      clearAmountDebounces();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    getArticleNoLookup({ variables: { companyId } });
    getCompanyCards({ variables: { companyId, searchTerm: '' } })

    if (!selectedSalesDocumentId || selectedSalesDocumentId < 1) {
      //We are in "create documentMode"
      initializeNewDocument();
    }
    else {
      getCompanySalesDocument({ variables: { salesDocumentId: selectedSalesDocumentId } });
    }

  }, [open, selectedSalesDocumentId]);

  useEffect(() => {
    function round(num, decimalPlaces = 0) {
      num = Math.round(num + "e" + decimalPlaces);
      return Number(num + "e" + -decimalPlaces);
    }

    if (document) {
      const sum = round(document.rows.reduce((acc, cur) => acc + cur.amount * cur.price * (1 - (cur.discount / 100.0)), 0), 2);
      //15
      //const vat = round(sum * 0.25, 2);
      const vat = round(document.rows.reduce((acc, cur) => acc + cur.amount * cur.price * (1 - (cur.discount / 100.0)) * (cur.vAT / 100.0), 0), 2)
      //3.75
      const sumRoundedInclVAT = sum + vat;
      //18.75
      const trunc = Math.trunc(sumRoundedInclVAT);
      //18
      const decimalPart = sumRoundedInclVAT - trunc;
      //0.75
      const rounding = round(Math.abs(decimalPart) >= 0.5 ? 1 - decimalPart : trunc - sumRoundedInclVAT, 2);
      //0.25

      setSumValues({ sum, vat, rounding, total: sumRoundedInclVAT + rounding })
      //19
    }
  }, [document]);

  const getPriceStep = (priceSteps, amount) => {
    if (!priceSteps?.length)
      return null;

    return [...priceSteps]
      .sort((a, b) => a.amount - b.amount)
      .filter(priceStep => priceStep.amount <= amount)
      .at(-1) ?? null;
  };

  const handleAcceptPriceUpdate = () => {
    if (!pendingArticlePriceUpdate)
      return;

    const { index, price, description } = pendingArticlePriceUpdate;

    const newDocument = lodash.cloneDeep(document);
    const row = newDocument.rows[index];

    if (!row) {
      setPendingArticlePriceUpdate(null);
      return;
    }

    row.price = price;
    row.description = description;
    row.priceDirty = false;

    setDocument(newDocument);

    setPendingArticlePriceUpdate(null);
    pendingArticlePriceUpdateRef.current = false;
  };

  const handleRejectPriceUpdate = () => {
    setPendingArticlePriceUpdate(null);
    pendingArticlePriceUpdateRef.current = false;
  };

  const handlePrint = (salesDocumentId) => {
    initPrint({
      variables: {
        salesDocumentIds: [salesDocumentId || document.salesDocumentId],
      }
    })
  }

  const handleSave = () => {
    let model = {
      companyId: companyId,
      documentStatus: "USER_GENERATED",
      documentType: "INVOICE",
      salesDocumentId: document.salesDocumentId ?? -1,
      header: document.header,
      rows: document.rows.map(n => ({
        amount: n.amount,
        price: n.price,
        articleNo: n.articleNo,
        description: n.description,
        unit: n.unit,
        discount: n.discount ?? 0,
        vAT: n.vAT,
      }))
    }
    updateSalesDocument({ variables: { model: model } })
    setDirty(true);
  }

  const handleSaveDraft = () => {
    let model = {
      companyId: companyId,
      documentStatus: "DRAFT",
      documentType: "INVOICE",
      salesDocumentId: document.salesDocumentId ?? -1,
      header: document.header,
      rows: document.rows.map(n => ({
        amount: n.amount,
        price: n.price,
        articleNo: n.articleNo,
        description: n.description,
        unit: n.unit,
        discount: n.discount ?? 0,
        vAT: n.vAT,
      }))
    }
    updateSalesDocument({ variables: { model: model } })
    setDirty(true);
  }

  const updatePriceFromAmount = async (
    currentDocument,
    index,
    amount,
    changeVersion
  ) => {
    const currentRow = currentDocument.rows[index];

    if (!currentRow?.articleNo)
      return;

    const articleNo = currentRow.articleNo;

    const priceListEntry = await lookupArticlePrice(articleNo);

    if (getRowChangeVersion(index) !== changeVersion) {
      // A newer amount/article change has occurred while the price lookup
      // was running. Ignore this stale result.
      return;
    }

    const priceStep = getPriceStep(
      priceListEntry?.priceSteps,
      amount
    );

    if (!priceStep)
      return;

    const newPrice = priceStep.price / Math.max(1, priceStep.amount);

    if (newPrice === currentRow.price)
      return;

    if (currentRow.priceDirty) {
      if (pendingArticlePriceUpdateRef.current)
        return;

      pendingArticlePriceUpdateRef.current = true;

      setPendingArticlePriceUpdate({
        index,
        price: newPrice,
        description: priceStep.description,
      });

      return;
    }

    const newDocument = lodash.cloneDeep(currentDocument);
    const newRow = newDocument.rows[index];

    newRow.price = newPrice;
    newRow.description = priceStep.description;
    newRow.priceDirty = false;

    setDocument(newDocument);
  };

  const handleUpdateSelectedCompanyCard = (newSelectedCard) => {
    setSelectedCompanyCard(newSelectedCard);

    if (newSelectedCard) {
      let newDocument = lodash.cloneDeep(document);

      newDocument.header.address = {
        address1: newSelectedCard.address.address1,
        address2: newSelectedCard.address.address2,
        address3: newSelectedCard.address.address3,
        address4: newSelectedCard.address.address4,
        address5: newSelectedCard.address.address5,
        city: newSelectedCard.address.city,
        contact: newSelectedCard.address.contact,
        country: newSelectedCard.address.country,
        customerId: newSelectedCard.address.customerId,
        email: newSelectedCard.address.email,
        reference: newSelectedCard.address.reference,
        telephone: newSelectedCard.address.telephone,
        zip: newSelectedCard.address.zip,
      }
      newDocument.header.companyAddressCardId = newSelectedCard.companyAddressCardId;
      newDocument.header.gLN = newSelectedCard.gLN;
      newDocument.header.orgNo = newSelectedCard.orgNo;
      newDocument.header.paymentTermsDays = newSelectedCard.paymentTermsDays;

      if (newDocument.header.documentDate) {
        newDocument.header.dueDate = moment(newDocument.header.documentDate).add(newDocument.header.paymentTermsDays, 'days').toDate();
      }

      setDocument(newDocument);
    }
  }

  const handleUpdateHead = (field, value) => {
    let newDocument = lodash.cloneDeep(document);

    newDocument.header[field] = value;

    if ((field === "documentDate" || field === "paymentTermsDays") && newDocument.header.documentDate) {
      newDocument.header.dueDate = moment(newDocument.header.documentDate).add(newDocument.header.paymentTermsDays, 'days').toDate();
    }

    setDocument(newDocument);
  }

  const handleUpdateAddress = (field, value) => {
    let newDocument = lodash.cloneDeep(document);
    newDocument.header.address[field] = value;
    setDocument(newDocument);
  }

  const handleUpdateRow = async (row, index, field, value) => {
    const newDocument = lodash.cloneDeep(document);
    const newRow = newDocument.rows[index];

    if (!newRow)
      return;

    if (field === "articleNo") {
      const changeVersion = bumpRowChangeVersion(index);

      pendingArticlePriceUpdateRef.current = false;
      setPendingArticlePriceUpdate(null);

      const articleNo = value?.trim() ?? "";

      newRow.articleNo = articleNo;
      newRow.priceDirty = false;

      if (!articleNo) {
        newRow.price = 0;
        newRow.description = "";

        setDocument(newDocument);

        return;
      }

      const priceListEntry = await lookupArticlePrice(articleNo);

      const priceStep = getPriceStep(
        priceListEntry?.priceSteps,
        newRow.amount
      );

      if (priceStep) {
        newRow.price = priceStep.price / Math.max(1, priceStep.amount);
        newRow.description = priceStep.description;
      }
      else {
        newRow.price = 0;
        newRow.description = "";
      }

      setDocument(newDocument);

      return;
    }

    if (field === "amount") {
      newRow.amount = value;

      const changeVersion = bumpRowChangeVersion(index);

      setDocument(newDocument);

      if (amountDebounceRef.current[index]) {
        clearTimeout(amountDebounceRef.current[index]);
      }

      amountDebounceRef.current[index] = setTimeout(() => {
        amountDebounceRef.current[index] = null;
        updatePriceFromAmount(newDocument, index, value, changeVersion);
      }, 500);

      return;
    }

    if (field === "price") {
      if (newRow.price !== value) {
        newRow.priceDirty = true;
      }

      newRow.price = value;

      setDocument(newDocument);

      return;
    }

    newRow[field] = value;

    setDocument(newDocument);
  };

  const handleDeleteRow = (row, index) => {
    if (amountDebounceRef.current[index]) {
      clearTimeout(amountDebounceRef.current[index]);
      delete amountDebounceRef.current[index];
    }

    let rows = document.rows.slice();
    rows.splice(index, 1);

    let newDocument = lodash.cloneDeep(document);
    newDocument = { ...newDocument, rows };

    setDocument(newDocument);
  };

  const handleMoveRow = ({ fromIndex, toIndex }) => {
    clearAmountDebounces();

    let rows = document.rows.slice();
    const row = rows.splice(fromIndex, 1);
    rows.splice(toIndex, 0, ...row);

    let newDocument = lodash.cloneDeep(document);
    newDocument = { ...newDocument, rows };

    setDocument(newDocument);
  };

  const handleAddRow = (row, index) => {
    let rows = document.rows.slice();

    rows.splice(index + 1, 0, {
      articleNo: "",
      amount: 1,
      description: "",
      price: 0,
      vAT: 25.0,
      discount: 0,
      total: 0,
      unit: "PIECES",
      priceDirty: false,
    });

    let newDocument = lodash.cloneDeep(document);
    newDocument = { ...newDocument, rows };

    setDocument(newDocument);
  };

  const handleAddTextRow = () => {
    let rows = document.rows.slice();

    rows.push({
      articleNo: "",
      amount: 1,
      description: "",
      price: 0,
      vAT: 0.0,
      discount: 0,
      total: 0,
      unit: "FREE_TEXT_ROW",
    });

    let newDocument = lodash.cloneDeep(document);
    newDocument = { ...newDocument, rows };

    setDocument(newDocument);
  }

  //Data fetched useEffects
  useEffect(() => {
    if (companySalesDocument) {
      var r = companySalesDocument.companyAdminContext.salesDocuments.getSalesDocument;
      var newDocument = { ...r };
      newDocument.header.documentDate = new Date(r.header.documentDate);
      newDocument.header.dueDate = r.header.dueDate ? new Date(r.header.dueDate) : undefined;

      setSelectedCompanyCard({ companyName: r.header.address.address1 })

      setDocument(newDocument);
    }
  }, [companySalesDocument])

  useEffect(() => {
    if (articleNoLookupData) {
      var articleNoLookup = articleNoLookupData
        ?.companyAdminContext
        ?.salesDocuments
        ?.getArticleNoLookup ?? [];

      setArticleNos(articleNoLookup);
    }
  }, [articleNoLookupData]);

  useEffect(() => {
    if (companyData) {
      let newOptions = [];

      /*if (value) {
        newOptions = [value];
      }*/

      if (companyData?.companyAdminContext?.salesDocuments?.searchCompanyCards) {
        newOptions = [...newOptions, ...companyData?.companyAdminContext?.salesDocuments?.searchCompanyCards];
      }

      setCompanyCards(newOptions);
    }
  }, [companyData])

  useEffect(() => {
    if (urlData?.companyAdminContext?.salesDocuments) {
      window.open(urlData.companyAdminContext.salesDocuments.salesDoucmentPrint, "_blank");
    }
  }, [urlData])

  //data: saveResult, loading: savingProgress, error: errorResult

  useEffect(() => {
    if (saveResult) {
      var newDocument = saveResult.companyAdminContext.salesDocuments.createUpdateDocument.result;
      if (newDocument) {
        if (newDocument.documentNumber) {
          // Final invoice saved
          snackbar.success(intl.formatMessage({ id: "admin.sales.alerts.savedDocument" }));
          handlePrint(newDocument.salesDocumentId);

          // After printing we get a new document
          initializeNewDocument();
        } else {
          // Draft saved
          snackbar.success(intl.formatMessage({ id: "admin.sales.alerts.savedDraft" }));

          // Update current document with returned values 
          const updatedDoc = {
            ...document,
            salesDocumentId: newDocument.salesDocumentId,
            header: {
              ...document.header,
              documentDate: new Date(newDocument.header.documentDate),
              dueDate: new Date(newDocument.header.dueDate),
            }
          };
          setDocument(updatedDoc);
        }
      } else {
        snackbar.error(intl.formatMessage({ id: "common.errorOnSaving" }));
      }
    } else if (errorResult) {
      snackbar.error(intl.formatMessage({ id: "common.errorOnSaving" }));
    }
  }, [saveResult])

  const readOnly = !!document?.documentNumber;

  return (<SimpleModalCompact open={open}
    onClose={n => handleClose(dirty)}
    fullScreen
    title={settings.modalTitle + (document?.documentNumber ? " " + document?.documentNumber : "")}>
    {loading && (<CircularProgress />)}
    {document && (
      <Box className={classes.root}>
        <Grid container spacing={1} sx={addrBottom}>
          <Grid item xs={12} sm={12} lg={4}>
            {readOnly ? (<>{selectedCompanyCard.value}</>) : (<CompanyAddressAutoComplete companyCards={companyCards} selectedCompany={selectedCompanyCard} onSelectCompany={handleUpdateSelectedCompanyCard} />)}
          </Grid>
          <Grid item xs={12} sm={6} lg={1}>
            <DatePicker
              className={classes.root}
              label={settings.documentDateLabel}
              localeText={{
                clearButtonLabel: intl.formatMessage({ id: "common.clear" }),
              }}
              onChange={date => handleUpdateHead("documentDate", date)}
              slotProps={{
                actionBar: {
                  actions: ['clear']
                }
              }}
              sx={{ width: "100%" }}
              value={document.header.documentDate}
              disabled={readOnly}
            />

          </Grid>

          <Grid item xs={12} sm={10} lg={1}>
            <TextField
              label={settings.paymentTermsDays}
              placeholderText={settings.paymentTermsDays}
              value={document.header.paymentTermsDays}
              onChange={(e) => { handleUpdateHead("paymentTermsDays", e.target.value) }}
              type="number"
              inputProps={{
                type: "text",
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              fullWidth
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={1}>
            <DatePicker wrapperClassName="datePicker"
              value={document.header.dueDate}
              onChange={date => handleUpdateHead("dueDate", date)}
              localeText={{
                clearButtonLabel: intl.formatMessage({ id: "common.clear" }),
              }}
              label={settings.dueDateLabel}
              sx={{ width: "100%" }}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12} sm={10} lg={1}>
            <TextField
              label={intl.formatMessage({ id: "admin.sales.dataLables.ourReference" })}
              placeholderText={settings.ourReferenceLabel}
              value={document.header.ourReference}
              onChange={(e) => { handleUpdateHead("ourReference", e.target.value) }}
              defaultValue={""}
              fullWidth
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12} sm={10} lg={1}>
            <TextField
              label={settings.yourReferenceLabel}
              placeholderText={settings.yourReferenceLabel}
              value={document.header.address.reference}
              onChange={(e) => { handleUpdateAddress("reference", e.target.value) }}
              defaultValue={""}
              fullWidth
              disabled={readOnly}
            />
          </Grid>
        </Grid>
        <SalesDocumentRowTable
          articleNos={articleNos}
          companyId={companyId}
          onAddRow={handleAddRow}
          onChangeRow={handleUpdateRow}
          onDeleteRow={handleDeleteRow}
          onMoveRow={handleMoveRow}
          rows={document.rows}
          readOnly={!!document.documentNumber}
          settings={settings}
        />
        {!readOnly && (<Button onClick={e => handleAddTextRow()}>Ny textrad</Button>)}
        <Divider sx={{ my: 2 }} />

        <Grid container direction="row" justifyContent="flex-end">
          <Grid item xs={12}>
            <Table className={classes.table} size="small">
              <TableBody>
                <TableRow>
                  <TableCell width="75%" className={classes.invisibleCell}></TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <FormattedMessage id="admin.sales.rowsTable.sumExclVAT" />
                  </TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <FormattedCurrency value={sumValues.sum} />
                  </TableCell>
                  <TableCell sx={lastRowWidth} className={classes.invisibleCell} ></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="75%" className={classes.invisibleCell}></TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <FormattedMessage id="admin.sales.dataLabels.vat" />
                  </TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <FormattedCurrency value={sumValues.vat} />
                  </TableCell>
                  <TableCell sx={lastRowWidth} className={classes.invisibleCell}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="75%" className={classes.invisibleCell}></TableCell>
                  <TableCell align="right">
                    <FormattedMessage id="admin.sales.dataLabels.rounding" />
                  </TableCell>
                  <TableCell align="right">
                    <FormattedCurrency value={sumValues.rounding} />
                  </TableCell>
                  <TableCell sx={lastRowWidth} className={classes.invisibleCell}></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="75%" className={classes.invisibleCell}></TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <span className={classes.styleForTotal}><FormattedMessage id="admin.sales.dataLabels.total" /></span>
                  </TableCell>
                  <TableCell align="right" className={classes.invisibleCell}>
                    <span className={classes.styleForTotal}><FormattedCurrency value={sumValues.total} /></span>
                  </TableCell>
                  <TableCell sx={lastRowWidth} className={classes.invisibleCell}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box align="right">
              <Button sx={{ mr: 1 }} variant="contained" disabled={document.salesDocumentId <= 0} onClick={() => handlePrint()}><FormattedMessage id="btn.print" /></Button>
              {!document.documentNumber && (<>
                <Button sx={{ mr: 1 }} variant="contained" onClick={() => { handleSaveDraft() }}><FormattedMessage id="admin.sales.invoices.buttons.saveDraft" /></Button>
                <Button variant="contained" onClick={() => { handleSave() }}><FormattedMessage id="admin.sales.invoices.buttons.createInvoice" /></Button></>)}
            </Box>
          </Grid>
        </Grid>
      </Box>
    )}

    <Dialog open={!!pendingArticlePriceUpdate} onClose={handleRejectPriceUpdate}>
      <DialogTitle>Uppdatera A-pris?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <b>Stafflat pris tillgängligt</b>
          <p>Det angivna antalet ändrar a-priset.<br />Vill du skriva över ditt manuella pris med systemets stafflade pris?</p>
          <i>Nytt a-pris: {pendingArticlePriceUpdate?.price}</i>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRejectPriceUpdate}>Nej</Button>
        <Button onClick={handleAcceptPriceUpdate} autoFocus variant="contained">Ja</Button>
      </DialogActions>
    </Dialog>
  </SimpleModalCompact>
  );
}

SalesEditModal.displayName = "SalesEditModal";

export default SalesEditModal;
